/**
 * Service d'export CSV des clients
 * Gère la récupération des données complètes et la génération du fichier CSV
 */

import tools from "../helpers/tools";

/**
 * Prépare les données client pour l'export CSV
 * Exclut les objets user et userGroup
 */
const prepareClientDataForCSV = (client) => {
  const data = {
    // Identification
    uuid: client.uuid || "",
    name: client.name || "",
    civilite: client.civilite || "",
    firstname: client.firstname || "",
    lastname: client.lastname || "",

    // Contact
    email: client.email || "",
    phone: client.phone || "",

    // Adresse
    address: client.address?.address || "",
    address2: client.address?.address2 || "",
    city: client.address?.city || "",
    postalCode: client.address?.postalCode || "",
    pays: client.pays || "",

    // Informations légales
    legalForm: client.legalForm || "",
    siret: client.siret || "",
    siren: client.siren || "",
    ape: client.ape || "",
    tva: client.tva || "",
    tvaIntracommunautaire: client.tvaIntracommunautaire || "",

    // Informations bancaires
    bic: client.bic || "",
    iban: client.iban || "",

    // Euler Hermes
    eulerHermes: client.eulerHermes || "",
    eulerHermesDate: client.eulerHermesDate
      ? tools.formatDate(client.eulerHermesDate)
      : "",

    // Hitech
    existeSurHitech: client.existeSurHitech ? "Oui" : "Non",
    identifiantHitech: client.identifiantHitech || "",

    // Commentaire
    comment: client.comment || "",

    // Dates
    createdAt: client.createdAt ? tools.formatDate(client.createdAt) : "",
    updatedAt: client.updatedAt ? tools.formatDate(client.updatedAt) : "",
  };

  return data;
};

/**
 * Convertit un tableau d'objets en chaîne CSV
 */
const convertToCSV = (data) => {
  if (!data || data.length === 0) {
    return "";
  }

  // En-têtes
  const headers = Object.keys(data[0]);
  const csvHeaders = headers.join(";");

  // Lignes de données
  const csvRows = data.map((row) => {
    return headers
      .map((header) => {
        const value = row[header];
        // Échapper les guillemets et entourer de guillemets si nécessaire
        if (value === null || value === undefined) {
          return "";
        }
        const stringValue = String(value);
        if (
          stringValue.includes(";") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      })
      .join(";");
  });

  return [csvHeaders, ...csvRows].join("\n");
};

/**
 * Télécharge le fichier CSV
 */
const downloadCSV = (csvContent, filename) => {
  // Ajouter le BOM UTF-8 pour Excel
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Exporte les clients au format CSV
 */
const exportClientsToCSV = (clientsData) => {
  const preparedData = clientsData.map(prepareClientDataForCSV);
  const csvContent = convertToCSV(preparedData);
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
  const filename = `clients_export_${timestamp}.csv`;

  downloadCSV(csvContent, filename);
};

/**
 * Calcule la largeur des colonnes pour l'affichage
 */
const calculateColumnWidths = (data) => {
  if (!data || data.length === 0) return {};

  const widths = {};
  const headers = Object.keys(data[0]);

  headers.forEach((header) => {
    let maxLength = header.length;
    data.forEach((row) => {
      const value = String(row[header] || "");
      if (value.length > maxLength) {
        maxLength = value.length;
      }
    });
    widths[header] = Math.min(maxLength + 2, 50); // Max 50 caractères
  });

  return widths;
};

export const clientExportService = {
  prepareClientDataForCSV,
  convertToCSV,
  downloadCSV,
  exportClientsToCSV,
  calculateColumnWidths,
};

export default clientExportService;
